import { ApiError } from '@community_dev/requests'
import { REGISTRATION_SOURCE } from '@community_dev/types/lib/api/RegistrationSource'
import { SMS_PROVIDER } from '@community_dev/types/lib/api/SmsProvider'
import { useQuery, UseQueryResult } from '@tanstack/react-query'

import { useBillingClient } from '../Billing/queries'
import { useCommunityTCRRegistrationQuery } from '../TCR/useCommunityTCRRegistrationQuery'

import { AttentionItem, getAttentionItems, getSectionBadges } from './health'

import { Client } from 'api/clients'
import { getProvisioningStatus, ProvisioningStatus } from 'api/infobipProvisioning'
import { CommunityRegistrationResponse } from 'api/tcr'
import { QUERIES } from 'constants/queries'

export type ClientHealth = {
  attentionItems: AttentionItem[]
  sectionBadges: ReturnType<typeof getSectionBadges>
  failedChecks: string[]
  isLoading: boolean
  isNotTwilio: boolean
  isCommunityCSPClient: boolean
  billingClient: ReturnType<typeof useBillingClient>
  provisioning: UseQueryResult<ProvisioningStatus>
  tcr: UseQueryResult<CommunityRegistrationResponse, ApiError>
}

export function useClientHealth(client: Client): ClientHealth {
  const isNotTwilio = client.smsProvider !== SMS_PROVIDER.TWILIO
  const isCommunityCSPClient = client.registrationSource === REGISTRATION_SOURCE.COMMUNITY

  const billingClient = useBillingClient(client.id)
  const provisioning = useQuery<ProvisioningStatus>(
    [QUERIES.PROVISIONING_STATUS, client.id],
    () => getProvisioningStatus({ clientId: client.id }),
    { enabled: isNotTwilio },
  )
  const tcr = useCommunityTCRRegistrationQuery(client.id, { enabled: isCommunityCSPClient })

  const accountStatus = billingClient.data?.accountStatus
  const overview = provisioning.data?.overview
  const tcrStatus = tcr.data?.data?.status

  const attentionItems: AttentionItem[] = getAttentionItems({
    accountStatus,
    monthlyThreshold: client.campaignMessagesMonthlyThreshold,
    monthlyUsed: client.campaignMessagesMonthlyUsed,
    provisioningErrors: overview && { numbers: overview.errorNumbers, campaigns: overview.errorCampaigns },
    tcrStatus,
  })

  const sectionBadges = getSectionBadges(attentionItems)

  const isLoading = billingClient.isInitialLoading || provisioning.isInitialLoading || tcr.isInitialLoading

  const billingError = billingClient.error as ApiError | null
  const provisioningError = provisioning.error as ApiError | null
  const failedChecks = [
    billingClient.isError && billingError?.status !== 404 && 'Billing',
    provisioning.isError && provisioningError?.status !== 404 && 'Number Provisioning',
    tcr.isError && 'TCR',
  ].filter((check): check is string => Boolean(check))

  return {
    attentionItems,
    sectionBadges,
    failedChecks,
    isLoading,
    isNotTwilio,
    isCommunityCSPClient,
    billingClient,
    provisioning,
    tcr,
  }
}
